import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { delay, filter, Subject, take, tap } from 'rxjs';
import { WebcamImage } from 'ngx-webcam';
import { fileUploadAction } from 'app/store/timesheet/timesheet.action';
import { fileConfig } from 'app/utils/config/file.config';
import { Store } from '@ngrx/store';
import { selectAFileNameAndUrl } from 'app/store/timesheet/timesheet.selector';
import { IFileModel } from 'app/store/timesheet/timesheet.model';
import { base64ToFile } from 'app/utils/helpers/helper-functions';
import { Actions, ofType } from '@ngrx/effects';
import { TimesheetTypes } from 'app/store/timesheet/timesheet.type';

@Component({
  selector: 'app-capture-photo-dialog',
  templateUrl: './capture-photo-dialog.component.html',
  styleUrls: ['./capture-photo-dialog.component.scss'],
  standalone: false,
})
export class CapturePhotoDialog implements OnInit {
  @ViewChild('canvas', { static: false })
  canvasRef!: ElementRef<HTMLCanvasElement>;
  isUploading: Boolean = false;
  fileName: string = '';
  constructor(
    public dialogRef: MatDialogRef<CapturePhotoDialog>,
    private actions: Actions,
    private store: Store,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  webcamImage: WebcamImage | null = null;
  compressedImage: string | null = null;

  trigger = new Subject<void>();
  triggerObservable = this.trigger.asObservable();
  ngOnInit(): void {}

  takeSnapshot() {
    this.trigger.next();
  }

  async handleImage(webcamImage: WebcamImage): Promise<void> {
    this.webcamImage = webcamImage;
    const base64 = webcamImage.imageAsDataUrl;

    const compressed = await this.compressImageToMaxSize(
      base64,
      320,
      240,
      100 * 1024,
    );

    const sizeInKB = this.getBase64Size(compressed) / 1024;

    if (sizeInKB > 100) {
      alert(
        'Image is still larger than 100KB. Please retake with better lighting or simpler background.',
      );
      return;
    }

    this.compressedImage = compressed;
  }

  async compressImageToMaxSize(
    base64: string,
    width: number,
    height: number,
    maxSize: number,
  ): Promise<string> {
    let quality = 0.7;
    let compressedBase64 = await this.compressImageWithCanvas(
      base64,
      width,
      height,
      quality,
    );

    while (this.getBase64Size(compressedBase64) > maxSize && quality > 0.1) {
      quality -= 0.05;
      compressedBase64 = await this.compressImageWithCanvas(
        base64,
        width,
        height,
        quality,
      );
    }

    return compressedBase64;
  }
  async compressImageWithCanvas(
    base64: string,
    width: number,
    height: number,
    quality: number,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = this.canvasRef.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Canvas context not available');
          return;
        }

        // Optionally resize canvas
        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      img.onerror = reject;
      img.src = base64;
    });
  }

  getBase64Size(base64String: string): number {
    let padding = 0;
    if (base64String.endsWith('==')) padding = 2;
    else if (base64String.endsWith('=')) padding = 1;

    const base64Length = base64String.length - (base64String.indexOf(',') + 1);
    return (base64Length * 3) / 4 - padding;
  }

  onHandleSubmit() {
    if (!this.compressedImage) {
      alert('Please take a photo first.');
      return;
    }
    const file = base64ToFile(this.compressedImage, 'photo.jpg', 'image/jpeg');
    const formData: FormData | null = fileConfig.upload(
      file,
      'timesheet',
      this.store,
    );
    if (formData) {
      this.isUploading = true;
      this.store.dispatch(fileUploadAction(formData));

      this.store
        .select(selectAFileNameAndUrl)
        .pipe(
          filter(
            (files: Pick<IFileModel, 'fileName' | 'fileUrl'>) =>
              !!files.fileUrl.length,
          ),
          delay(0),
          take(1),
          tap((files: Pick<IFileModel, 'fileName' | 'fileUrl'>) => {
            setTimeout(() => {
              this.fileName = files.fileName;
              this.dialogRef.close({
                webcamImage: this.compressedImage,
                fileName: files.fileName,
              });
              this.isUploading = false;
              this.cdr.detectChanges();
            });
          }),
        )
        .subscribe();

      this.actions
        .pipe(
          ofType(TimesheetTypes.File_Upload_Failed),
          tap(() => {
            this.isUploading = false;
            this.cdr.detectChanges();
          }),
        )
        .subscribe();
    }
  }
}
